import {
  useListPartnerMonthlyWithdraws,
  useReadDocuments,
} from '@/api/accounting-dealer'
import { DocumentInput } from '@/components'
import { TextInput } from '@/components/inputs'
import { useCompany } from '@/context/companies'
import { DocumentRequest } from '@/types'
import { ErrorOutline } from '@mui/icons-material'
import { Stack, Box, Skeleton, Typography } from '@mui/material'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

type PartnerDocumentsProps = {
  partnerKey: string
  documentRequest: DocumentRequest
  partnerIndex: number
  partnerName: string
}

export function PartnerDocuments({
  partnerKey,
  documentRequest,
  partnerIndex,
  partnerName,
}: PartnerDocumentsProps) {
  const { control, setValue, watch } = useFormContext()

  const { company } = useCompany()

  const { data: partnerMonthlyWithdraws } = useListPartnerMonthlyWithdraws({
    companyKey: company.key,
    partnerKey,
    year: parseInt(documentRequest.year),
  })

  const {
    data: partnerDocuments,
    error: partnerDocumentsError,
    isLoading: isPartnerDocumentsLoading,
  } = useReadDocuments({
    entity_key: partnerKey,
    entity_type: 'partner',
    document_request_key: documentRequest.key,
    status: 'approved',
  })

  const noFinancialMovement = watch('no_financial_movement')

  useEffect(() => {
    if (!partnerMonthlyWithdraws) return

    const monthlyWithdraw = partnerMonthlyWithdraws.find(
      (withdraw) => withdraw.month === parseInt(documentRequest.month),
    )

    setValue(
      `partners.${partnerIndex}.monthly_withdraw`,
      monthlyWithdraw?.amount || '',
    )
  }, [partnerMonthlyWithdraws])

  if (isPartnerDocumentsLoading) {
    return (
      <Stack spacing={2}>
        <Box>
          <Skeleton width="80%" />
          <Skeleton variant="rounded" height={60} />
        </Box>
        <Skeleton width="90%" />
        <Skeleton variant="rounded" height={56} />
      </Stack>
    )
  }

  if (partnerDocumentsError) {
    return (
      <Box textAlign="center">
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar os documentos do sócio.
        </Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      {!noFinancialMovement && (
        <DocumentInput
          fieldName={`partners.${partnerIndex}.payslip`}
          label="Holerites / Contracheques de seus outros vínculos profissionais em que tenha recolhimento de INSS"
          entity={{
            key: partnerKey,
            type: 'partner',
          }}
          documents={partnerDocuments || []}
          documentType="payslip"
          document_request_key={documentRequest.key}
        />
      )}

      <Typography mt={2}>
        Qual foi o valor total que a {company.corporate_name} transferiu para a
        conta pessoa física de <b>{partnerName}</b> neste mês?
      </Typography>
      <TextInput
        control={control}
        name={`partners.${partnerIndex}.monthly_withdraw`}
        label="Valor total transferido no mês"
        format="currency"
        helperText=" Atenção: Empresas com débitos federais são proibidas de distribuir lucros, sob pena de multa de 50% sobre o valor pago (Lei 4.357/64). Além disso, retiradas de lucros mensais acima de R$ 50.000,00 por sócio agora sofrem retenção obrigatória de 10% de IRRF (Lei 15.270/2025). O total acima será composto de lucros e pró-labore. Em caso de dúvidas, fale com nossa equipe."
      />
    </Stack>
  )
}
